import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  Suspense,
} from 'react';
import Markdown from '@/components/markdown';
import { Skeleton } from 'antd';
import {
  atomOneDark,
  atomOneLight,
} from '@fengkx/react-syntax-highlighter/dist/esm/styles/hljs';

const SyntaxHighlighter = React.lazy(
  () => import('@fengkx/react-syntax-highlighter')
);
const MemoSyntaxHighlighter = React.memo(SyntaxHighlighter);

const LoadingIndicator = ({ darkMode = true, height = 200 }) => (
  <div
    className="rounded pr-6px"
    style={{ height: `${height}px` }}
  >
    <Skeleton className={[darkMode ? 'antd-dark' : '', 'mt-24px']} active />
  </div>
);

const MonoCode = React.memo(({ children }) => {
  return (
    <pre className="whitespace-pre-wrap text-[14px] rounded-[3px] leading-relaxed text-[#999]">
      <code className="language-markup">{children}</code>
    </pre>
  );
});

const HighlightCode = React.memo(
  ({
    className,
    code,
    language,
    darkMode = false,
    showLineNumbers = false,
  }) => {
    const highlighterConfig = useMemo(() => {
      const highlighterDark = {
        className: 'highlighter-box highlighter-box-dark',
        style: atomOneDark,
        showLineNumbers,
      };
      const highlighterLight = {
        className: 'highlighter-box highlighter-box-light',
        style: atomOneLight,
        showLineNumbers,
      };

      return darkMode ? highlighterDark : highlighterLight;
    }, [darkMode, showLineNumbers]);

    return (
      <Markdown
        typing={false}
        text={code}
        hideLanguage
        highlighterConfig={highlighterConfig}
        components={{
          code({ node, inline, className, children, ...props }) {
            const LANGUAGE_RE = /language-(\w+)/;
            const match = LANGUAGE_RE.exec(className);
            if (match) {
              return (
                <MemoSyntaxHighlighter
                    useInlineStyles={true}
                    wrapLongLines={true}
                    style={highlighterConfig.style}
                    showLineNumbers={highlighterConfig.showLineNumbers}
                    className={`leading-relaxed ${highlighterConfig?.className ?? 'highlighter-box'}`}
                    customStyle={{
                      fontFamily: 'ui-monospace, monospace',
                    }}
                    language={match[1]}
                    children={String(children).replace(/\n$/, '')}
                    {...props}
                  />
              );
            }
            return (
              <code {...props} className={className}>
                {children}
              </code>
            );
          },
        }}
      />
    );
  }
);

const CodeBlock = ({
  inputting,
  className = '',
  content = '',
  darkMode = true,
  language = 'html',
  showLineNumbers = true,
}) => {
  const isHighlight = useMemo(() => {
    if (inputting) {
      return false;
    }
    if (!language) {
      return false;
    }
    return true;
  }, [language, inputting]);

  const formattedCode = useMemo(() => {
    if (isHighlight) {
      const lang = language ? language.replace(/^language-/, '') : 'text';
      return `\`\`\`${lang}\n${content?.trim() || ''}\n\`\`\``;
    }

    return content || '';
  }, [content, isHighlight, language]);

  const isLargeContent = useMemo(() => {
    return content && content.length > 300;
  }, [content]);

  // 延迟渲染
  const [shouldRender, setShouldRender] = useState(!isLargeContent);

  useEffect(() => {
    if (isLargeContent && !shouldRender) {
      const timer = setTimeout(() => setShouldRender(true), 20);
      return () => clearTimeout(timer);
    }
  }, [isLargeContent, shouldRender]);

  if (!shouldRender) {
    return <LoadingIndicator height={500} />;
  }

  return (
    <div className={className}>
      {/* 使用Suspense时间比直接渲染要长 ??? */}
      <div fallback={<LoadingIndicator height={500} />}>
        {!isHighlight ? (
          <MonoCode>{content}</MonoCode>
        ) : (
          <HighlightCode
            language={language}
            code={formattedCode}
            darkMode={darkMode}
            showLineNumbers={showLineNumbers}
          />
        )}
      </div>
    </div>
  );
};
export default React.memo(CodeBlock);
